<template>
    <v-container fluid>
        <v-card class="pa-4">
            <v-card-title class="text-h5 mb-4">
                Колонки биллинга
            </v-card-title>

            <v-data-table :headers="headers" :items="billColumns" :loading="loading" loading-text="Загрузка данных..."
                no-data-text="Нет данных..." class="elevation-1">
                <template v-slot:[`item.billColumnVid`]="{ item }">
                    {{ item.billColumnVid?.name || 'Не указано' }}
                </template>

                <template v-slot:[`item.billTarifDetals`]="{ item }">
                    <v-chip v-for="detail in item.billTarifDetals" :key="detail.id" small class="mr-1 mb-1">
                        {{ detail.cargoType?.name || 'Без типа' }}
                    </v-chip>
                    <span v-if="!item.billTarifDetals || item.billTarifDetals.length === 0">
                        Нет деталей
                    </span>
                </template>

                <template v-slot:[`item.createTimeStamp`]="{ item }">
                    {{ formatDate(item.createTimeStamp) }}
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex justify-space-between">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon color="primary" v-bind="attrs" v-on="on" @click="editItem(item)">
                                    mdi-pencil
                                </v-icon>
                            </template>
                            <span>Изменить</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon color="red" v-bind="attrs" v-on="on" @click="deleteItem(item)">
                                    mdi-delete
                                </v-icon>
                            </template>
                            <span>Удалить</span>
                        </v-tooltip>
                    </div>
                </template>
            </v-data-table>

            <!-- место для кнопки добавить -->
            <v-sheet height="64" />

            <!-- кнопка добавить -->
            <v-btn v-if="canAdd" bottom right color="primary" dark fab fixed to="/settings/billingColumns/add">
                <v-icon class="white--text">
                    mdi-plus
                </v-icon>
            </v-btn>
        </v-card>

        <!-- Диалог добавления/редактирования -->
        <v-dialog v-model="dialog" max-width="500px">
            <v-card>
                <v-card-title>
                    <span class="text-h5">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field v-model="editedItem.name" label="Название колонки"
                                    required></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-select v-model="editedItem.vid" :items="billingKinds" item-title="name"
                                    item-value="id" label="Тип колонки" required></v-select>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="blue-darken-1" variant="text" @click="close">
                        Отмена
                    </v-btn>
                    <v-btn color="blue-darken-1" variant="text" @click="save">
                        Сохранить
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
export default {
    name: 'BillColumns',
    data() {
        return {
            billColumns: [],
            billingKinds: [], // Для выпадающего списка типов
            loading: false,
            dialog: false,
            editedIndex: -1,
            editedItem: {
                id: 0,
                name: '',
                vid: null,
                isDeleted: false,
                createTimeStamp: null
            },
            defaultItem: {
                id: 0,
                name: '',
                vid: null,
                isDeleted: false,
                createTimeStamp: null
            },
            headers: [
                { text: 'ID', value: 'id', align: 'start', sortable: true },
                { text: 'Название', value: 'name', sortable: true },
                { text: 'Тип колонки', value: 'billColumnVid.name', sortable: true },
                { text: 'Детали тарифа', value: 'billTarifDetals', sortable: false },
                { text: 'Дата создания', value: 'createTimeStamp', sortable: true },
                { text: 'Действия', value: 'actions', sortable: false, align: 'center', width: '120px' }
            ]
        }
    },
    async created() {
        await Promise.all([
            this.loadBillColumns(),
            this.loadBillingKinds()
        ]);
    },
    computed: {
        canAdd: function () {
            return this.$store.state.auth.user.role !== "Клиент" && this.$store.state.auth.user.role !== "Кладовщик"
        },
        formTitle() {
            return this.editedIndex === -1 ? 'Добавить колонку' : 'Редактировать колонку'
        }
    },
    watch: {
        dialog(val) {
            val || this.close()
        }
    },
    methods: {
        async loadBillColumns() {
            this.loading = true;
            try {
                const response = await this.axios.get('/billing/bill-columns');
                this.billColumns = response.data;
            } catch (error) {
                console.error('Error loading bill columns:', error);
                alert('Произошла ошибка при загрузке данных');
            } finally {
                this.loading = false;
            }
        },

        async loadBillingKinds() {
            try {
                const response = await this.axios.get('/billing/bill-column-vids');
                this.billingKinds = response.data;
            } catch (error) {
                console.error('Error loading billing kinds:', error);
            }
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('ru-RU') + ' ' + date.toLocaleTimeString('ru-RU');
        },

        editItem(item) {
            this.editedIndex = this.billColumns.indexOf(item);
            this.editedItem = Object.assign({}, item);
            this.dialog = true;
        },

        async deleteItem(item) {
            if (confirm(`Удалить колонку "${item.name}"?`)) {
                try {
                    await this.axios.delete(`/billing/bill-columns/${item.id}`);
                    await this.loadBillColumns();
                    alert('Колонка успешно удалена');
                } catch (error) {
                    console.error('Error deleting item:', error);
                    alert('Произошла ошибка при удалении');
                }
            }
        },

        close() {
            this.dialog = false;
            this.$nextTick(() => {
                this.editedItem = Object.assign({}, this.defaultItem);
                this.editedIndex = -1;
            });
        },

        async save() {
            if (this.editedIndex > -1) {
                // Редактирование существующей записи
                try {
                    // Здесь нужно будет добавить метод обновления, когда он появится в API
                    // await this.axios.put(`/billing/bill-columns/${this.editedItem.id}`, this.editedItem);
                    Object.assign(this.billColumns[this.editedIndex], this.editedItem);
                    alert('Редактирование будет доступно позже');
                } catch (error) {
                    console.error('Error updating item:', error);
                    alert('Произошла ошибка при обновлении');
                }
            } else {
                // Добавление новой записи
                try {
                    const response = await this.axios.post('/billing/bill-columns', {
                        name: this.editedItem.name,
                        vid: this.editedItem.vid
                    });
                    this.billColumns.push(response.data);
                    alert('Колонка успешно добавлена');
                } catch (error) {
                    console.error('Error adding item:', error);
                    alert('Произошла ошибка при добавлении');
                }
            }
            this.close();
        }
    }
}
</script>